import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, TriangleAlert } from 'lucide-react';
import { api, del, patch } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../lib/ui.js';
import type { PasskeyHome } from '@siva/shared';
import { Badge, Card, Table, Th } from '../lib/components.js';
import { useConfirm } from '../lib/Modal.js';
import { t } from '../i18n/index.js';

type Release = {
  version_code: number;
  version_name: string;
  url: string;
  sha256: string;
  mandatory: boolean;
  notes: string | null;
  published_at: string;
  is_latest: boolean;
};
type WorkerRow = {
  id: string;
  name: string;
  version_code: number | null;
  version_name: string | null;
  last_punch_at: string | null;
  below_minimum: boolean;
  /** What their passkey says the phone is, for telling "no app installed" from "no app exists". */
  home?: PasskeyHome;
  /** Whether they have a passkey at all: not enrolled is a different job from not installed. */
  enrolled?: boolean;
};
/** A build phones are demonstrably running, whoever delivered it. */
type SeenVersion = { version_code: number; version_name: string | null; phones: number };
type Body = {
  minimum_version_code: number;
  /** The rule: everybody on the newest build anybody is running, worked out at the punch. */
  require_newest?: boolean;
  releases: Release[];
  seen_versions?: SeenVersion[];
  workers: WorkerRow[];
};

/**
 * The app, on the Admin page.
 *
 * Two numbers with very different consequences. The latest build is an offer the app makes on
 * launch; the minimum is a refusal the server enforces at the punch. Raising the minimum stops
 * people working, so this screen will not let it be done blind: picking a build asks the server
 * who it would stop, and the confirmation says their names.
 */
/** "1 worker" / "2 workers" — t() substitutes, it does not conjugate. */
const people = (n: number) => (n === 1 ? 'worker' : 'workers');

export function AndroidCard() {
  const confirm = useConfirm();
  const [body, setBody] = useState<Body | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    version_code: '',
    version_name: '',
    sha256: '',
    notes: '',
  });

  const load = useCallback(async () => {
    try {
      const r = await api<Body>('/admin/android');
      setBody(r);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);



  /**
   * Upload the APK itself and publish it in one act.
   *
   * The server hashes what it receives and refuses it when that does not match the hash pasted
   * from the build output, so publishing a stale file under a new name cannot happen here. There
   * is deliberately no "type in a URL" path on this screen: hosting the installer somewhere else
   * and pointing at it is the habit this replaces.
   */
  const uploadRelease = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const body = new FormData();
      body.append('version_name', form.version_name.trim());
      body.append('version_code', form.version_code.trim());
      body.append('sha256', form.sha256.trim());
      if (form.notes.trim()) body.append('notes', form.notes.trim());
      body.append('file', file, file.name);
      const r = await api<{ release: { version_name: string; bytes: number } }>(
        '/admin/android/upload',
        { method: 'POST', body },
      );
      setAdding(false);
      setFile(null);
      setForm({ version_code: '', version_name: '', sha256: '', notes: '' });
      setNote(
        t('android_uploaded', {
          version: r.release.version_name,
          mb: (r.release.bytes / 1024 / 1024).toFixed(1),
        }),
      );
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (r: Release) => {
    const ok = await confirm({
      title: t('android_withdraw_title', { version: r.version_name }),
      body: t('android_withdraw_body'),
      confirmLabel: t('android_withdraw'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await del(`/admin/android/releases/${r.version_code}`);
      setNote(t('android_withdrawn', { version: r.version_name }));
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Turn the rule on or off.
   *
   * One boolean, and no number to keep in step: what it implies is worked out at the punch, from
   * the newest build any phone has reported. Reloading afterwards is what shows the owner who has
   * just fallen behind it.
   */
  const applyRequireNewest = async (on: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await patch('/admin/android/require-newest', { require_newest: on });
      await load();
      setNote(on ? t('android_require_newest_on') : t('android_require_newest_off'));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!body) return <Spinner />;
  const latest = body.releases.find((r) => r.is_latest) ?? null;
  const newestInUse = (body.seen_versions ?? [])[0] ?? null;
  /**
   * Who the rule stops, right now.
   *
   * Somebody with no app version is not stopped by a minimum at all — no app, nothing to be
   * behind — so only the phones that report a build can be caught by it.
   */
  const wouldStop = newestInUse
    ? body.workers.filter(
        (w) => w.version_code !== null && w.version_code < newestInUse.version_code,
      )
    : [];
  /**
   * Everybody, in the order somebody would work through them: whoever cannot punch first, then
   * the Android phones with no app installed, then the rest. The counts underneath became rows,
   * at the owner's request — a note and a list of names saying the same thing is one of them too
   * many, and the names were the useful half.
   */
  const rank = (w: WorkerRow) =>
    w.below_minimum ? 0 : w.version_code === null && w.home === 'android' ? 1 : w.version_code !== null ? 2 : 3;
  const rows = [...body.workers].sort(
    (a, b) => rank(a) - rank(b) || (a.version_code ?? 0) - (b.version_code ?? 0) || a.name.localeCompare(b.name),
  );

  return (
    <Card
      id="android"
      title={
        <>
          <Smartphone size={16} aria-hidden="true" /> {t('android_title')}
        </>
      }
      subtitle={t('android_sub')}
    >
      {error && <Alert>{error}</Alert>}
      {note && <Alert kind="success">{note}</Alert>}

      {/*
        One fact, because only one of the three was about the fleet. The build we host and its
        checksum describe a file nobody downloads since the move to the Play Store, and "minimum
        to punch" is now decided by the rule below rather than typed in — both live with the
        upload form at the foot of the card, where the fallback lives.
      */}
      <p className="android-now">
        {t('android_newest_in_use')}:{' '}
        {newestInUse ? (
          <strong>
            {newestInUse.version_name ?? '—'} ({t('android_build', { n: newestInUse.version_code })})
          </strong>
        ) : (
          <span className="muted">{t('android_nobody_on_app')}</span>
        )}
      </p>

      {/*
        A rule, not a build number to choose from a list. Picking a number asked the owner about
        our release history — a list that froze the day the app moved to the Play Store — when
        what the office wants is "be on the newest build, or go and update". The server works the
        number out at the punch, because the newest build changes by itself as phones update.
      */}
      <label className="android-rule">
        <input
          type="checkbox"
          checked={body.require_newest ?? false}
          disabled={busy}
          onChange={(e) => void applyRequireNewest(e.target.checked)}
        />
        <span>
          <strong>{t('android_require_newest')}</strong>
          <small className="muted">{t('android_require_newest_help')}</small>
        </span>
      </label>

      {/*
        The number this rule implies, and who it leaves behind. The dropdown had a server-side
        preview of exactly this, and it was the only thing that made typing a number safe; the
        rule needs it for the same reason, because one phone updating puts everybody else
        behind at once.
      */}
      {wouldStop.length > 0 && (
        <Alert kind="warn">
          <TriangleAlert size={16} aria-hidden="true" />{' '}
          {t('android_rule_would_stop', {
            n: wouldStop.length,
            people: people(wouldStop.length),
            names: wouldStop.map((w) => w.name).join(', '),
          })}
        </Alert>
      )}

      <h4>{t('android_who')}</h4>
      {rows.length === 0 ? (
        <p className="muted">{t('android_nobody_on_app')}</p>
      ) : (
        <Table className="android-table">
          <thead>
            <tr>
              <Th>{t('pay_worker')}</Th>
              <Th>{t('android_version')}</Th>
              <Th>{t('android_last_punch')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.id} className={w.below_minimum ? 'below-minimum' : ''}>
                <td data-label={t('pay_worker')}>
                  <Link to={`/workers/${w.id}`}>{w.name}</Link>
                </td>
                {/*
                  Everybody in one column, because "which build" and "no build at all" are the
                  same question asked of different phones. An Android phone with no version has
                  not installed the app and is somebody to chase; an iPhone has nothing to
                  install and never will have.
                */}
                <td data-label={t('android_version')}>
                  {w.version_code !== null ? (
                    <>
                      {w.version_name ?? '—'}{' '}
                      <small className="muted">
                        ({t('android_build', { n: w.version_code })})
                      </small>
                      {w.below_minimum && (
                        <Badge tone="red" className="no-ml">
                          {t('android_below')}
                        </Badge>
                      )}
                    </>
                  ) : w.home === 'apple' ? (
                    <span className="muted">{t('android_iphone_no_app')}</span>
                  ) : w.home === 'android' ? (
                    <span className="warn-text">{t('android_not_installed')}</span>
                  ) : (
                    // No passkey at all is not the same as a passkey we cannot place: the first
                    // is somebody who has not been set up yet, which is a different job.
                    <span className="muted">
                      {w.enrolled ? t('android_unknown_phone') : t('android_not_enrolled')}
                    </span>
                  )}
                </td>
                <td data-label={t('android_last_punch')} className="nowrap">
                  {w.last_punch_at ? fmtDate(w.last_punch_at, false) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="row android-actions">
        <button type="button" className="small secondary" onClick={() => setAdding(!adding)}>
          {adding ? t('cancel') : t('android_add_release')}
        </button>
      </div>

      {adding && latest && (
        <p className="muted small-text">
          {t('android_latest')}: {latest.version_name} (
          {t('android_build', { n: latest.version_code })}) ·{' '}
          {t('android_published', { date: fmtDate(latest.published_at, false) })} ·{' '}
          <code className="sha">{latest.sha256.slice(0, 16)}…</code>
          {body.minimum_version_code > 0 && (
            <>
              {' · '}
              {t('android_minimum')}: {t('android_build', { n: body.minimum_version_code })}
            </>
          )}
        </p>
      )}
      {adding && (
        <div className="grid-form android-release-form">
          <p className="muted wide small-text">{t('android_upload_intro')}</p>
          <label className="wide">
            {t('android_file')}
            <input
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <label>
            {t('android_version_name')}
            <input
              value={form.version_name}
              onChange={(e) => setForm({ ...form, version_name: e.target.value })}
              placeholder="0.1.4"
            />
          </label>
          <label>
            {t('android_version_code')}
            <input
              type="number"
              inputMode="numeric"
              value={form.version_code}
              onChange={(e) => setForm({ ...form, version_code: e.target.value })}
              placeholder="5"
            />
          </label>
          <label className="wide">
            {t('android_sha')}
            <input
              value={form.sha256}
              onChange={(e) => setForm({ ...form, sha256: e.target.value })}
              placeholder={t('android_sha_hint')}
            />
          </label>
          <label className="wide">
            {t('android_notes')}
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={500}
            />
          </label>
          <div className="row">
            <button
              type="button"
              className="small"
              disabled={busy || !file || !form.version_code || !form.version_name || !form.sha256}
              onClick={() => void uploadRelease()}
            >
              {busy ? t('android_uploading') : t('android_save_release')}
            </button>
          </div>
        </div>
      )}

      {/* The builds we host, with the rest of the fallback: nobody installs from here now. */}
      {adding && body.releases.length > 0 && (
        <ul className="list android-releases">
          {body.releases.map((r) => (
            <li key={r.version_code} className="row between">
              <span>
                <strong>{r.version_name}</strong>{' '}
                <small className="muted">{t('android_build', { n: r.version_code })}</small>{' '}
                {r.is_latest && (
                  <Badge tone="green" className="no-ml">
                    {t('android_is_latest')}
                  </Badge>
                )}
                {r.version_code === body.minimum_version_code && (
                  <Badge tone="amber" className="no-ml">
                    {t('android_is_minimum')}
                  </Badge>
                )}
                <br />
                <small className="muted">
                  {fmtDate(r.published_at, false)}
                  {r.notes ? ` · ${r.notes}` : ''}
                </small>
              </span>
              <button
                type="button"
                className="small ghost"
                disabled={busy}
                onClick={() => void withdraw(r)}
              >
                {t('android_withdraw')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
